import {Fragment} from 'react';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon/Icon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../shadcn/breadcrumb';
import {useDirection} from '../shadcn/direction';

import type {IconName} from '../../icons/icon-map';
import type {FC} from 'react';

export interface BreadcrumbStep {
  /** Optional text label */
  title?: string;
  icon?: IconName;
  /** Optional URL for clickable breadcrumb step */
  href?: string;
  /** Optional click handler — overrides default anchor navigation when provided */
  onClick?: () => void;
  /** Optional accessible label for icon-only steps */
  ariaLabel?: string;
  isActive?: boolean;
}

export interface BreadcrumbsProps {
  /** Array of breadcrumb steps (icon, title, or both) */
  steps: BreadcrumbStep[];
  /** Custom className for the root container */
  className?: string;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = ({steps, className = ''}) => {
  const direction = useDirection();
  const chevronIconName = direction === 'ltr' ? 'chevron-right' : 'chevron-left';

  return (
    <Breadcrumb className={cn('text-right', className)} data-slot="breadcrumbs">
      <BreadcrumbList className="gap-2">
        {/* Breadcrumb steps */}
        {steps.map((step, index) => (
          <Fragment key={index}>
            {index > 0 && (
              <BreadcrumbSeparator>
                <Icon name={chevronIconName} className="size-4 text-fg-tertiary" />
              </BreadcrumbSeparator>
            )}
            <BreadcrumbItem>
              {step.href || step.onClick ? (
                <BreadcrumbLink
                  href={step.href ?? '#'}
                  aria-label={step.ariaLabel ?? step.title}
                  onClick={
                    step.onClick
                      ? e => {
                          e.preventDefault();
                          step.onClick!();
                        }
                      : undefined
                  }
                  className={cn(
                    'inline-flex items-center gap-1.5',
                    step.title && 'text-fg-tertiary',
                    !step.title &&
                      'size-8 rounded-xl border-[0.75px] border-border-subtle bg-background-surface hover:bg-background-hover justify-center'
                  )}
                >
                  {step.icon && <Icon name={step.icon} className="size-4 shrink-2" />}
                  {step.title}
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage
                  className={cn(
                    'inline-flex items-center gap-1.5',
                    !step.title &&
                      'size-8 rounded-2xl border-[0.75px] border-border-subtle bg-background-surface justify-center',
                    step.isActive ? 'text-fg-secondary' : 'text-fg-tertiary'
                  )}
                  aria-label={step.ariaLabel ?? step.title}
                >
                  {step.icon && <Icon name={step.icon} className="size-4 shrink-0" />}
                  {step.title}
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
